// frontend/src/hooks/useCreateOrder.ts
import { useCallback, useState } from 'react'
import {
  useAccount,
  useConfig,
  useSwitchChain,
  useWalletClient,
} from 'wagmi'
import { getPublicClient } from '@wagmi/core'
import { useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { decodeEventLog } from 'viem'
import { api as apiClient, CreateP2POrderDto, P2POrder } from '../lib/api'
import {
  escrowAddressForChain,
  infoForSide,
  wagmiChain,
} from '../lib/p2p/tokens'
import { ERC20_MIN_ABI, P2P_ESCROW_ABI } from '../lib/p2p/abi'
import { humanizeP2PError } from '../lib/p2p/errors'

export type CreateOrderStage =
  | 'idle'
  | 'building'
  | 'switching'
  | 'checkingAllowance'
  | 'approving'
  | 'creating'
  | 'matching'
  | 'success'
  | 'error'

export type FlowStepLevel = 'info' | 'progress' | 'success' | 'warn' | 'error'

export interface FlowStep {
  /** Epoch ms when the step was added. */
  at: number
  level: FlowStepLevel
  /** One-line human-readable message. */
  message: string
  /** Optional expanded detail (raw error, tx hash, etc.). */
  detail?: string
}

// Backend listener typically catches up within 5-10s of a confirmed tx.
// 25 polls × 1.2s = ~30s window before we give up "match" reporting.
const MATCH_POLL_MAX = 25
const MATCH_POLL_INTERVAL_MS = 1200

export function useCreateOrder() {
  const { address } = useAccount()
  const { switchChainAsync } = useSwitchChain()
  const { data: walletClient } = useWalletClient()
  const config = useConfig()
  const queryClient = useQueryClient()

  const [stage, setStage] = useState<CreateOrderStage>('idle')
  const [error, setError] = useState<string | null>(null)
  const [txHash, setTxHash] = useState<`0x${string}` | null>(null)
  const [steps, setSteps] = useState<FlowStep[]>([])

  const pushStep = useCallback(
    (level: FlowStepLevel, message: string, detail?: string) => {
      setSteps((prev) => [...prev, { at: Date.now(), level, message, detail }])
    },
    [],
  )

  const createOrder = useCallback(
    async (dto: CreateP2POrderDto) => {
      setError(null)
      setTxHash(null)
      setSteps([])

      if (!address || !walletClient) {
        const msg = 'Wallet no conectada'
        setStage('error')
        setError(msg)
        pushStep('error', msg)
        return { ok: false as const, error: msg }
      }

      const isTake = Boolean(dto.targetMakerOrderId)
      const flowLabel = isTake ? 'TAKE' : 'CREATE'
      pushStep('info', `Iniciando flujo ${flowLabel} (${dto.side}, ${formatAmount(dto.amount)})`)

      try {
        setStage('building')
        pushStep('progress', 'Pidiendo calldata al backend…')
        const calldata = await apiClient.createP2POrderCalldata(dto)
        const targetChain = wagmiChain(calldata.chain)
        pushStep(
          'success',
          `Calldata recibida — destino: ${calldata.chain} ${shortAddr(calldata.to)}`,
        )

        const publicClient = getPublicClient(config, { chainId: targetChain.id })
        if (!publicClient) {
          throw new Error(`No public client for chain ${calldata.chain}`)
        }

        if (walletClient.chain?.id !== targetChain.id) {
          setStage('switching')
          pushStep('progress', `Cambiando red de la wallet a ${calldata.chain}…`)
          await switchChainAsync({ chainId: targetChain.id })
          pushStep('success', `Red activa: ${calldata.chain} (chainId ${targetChain.id})`)
        }

        const tokenInfo = infoForSide(dto.side)
        const escrowAddress = escrowAddressForChain(calldata.chain)
        setStage('checkingAllowance')
        pushStep('progress', `Consultando allowance de ${tokenInfo.tokenSymbol} para el escrow…`)
        const allowance = (await publicClient.readContract({
          address: tokenInfo.tokenAddress,
          abi: ERC20_MIN_ABI,
          functionName: 'allowance',
          args: [address, escrowAddress],
        })) as bigint

        const amountWei = BigInt(dto.amount)
        if (allowance < amountWei) {
          setStage('approving')
          pushStep(
            'progress',
            `Allowance ${formatAmount(allowance.toString())} < requerido ${formatAmount(dto.amount)} → solicitando approve`,
          )
          const approveHash = await walletClient.writeContract({
            address: tokenInfo.tokenAddress,
            abi: ERC20_MIN_ABI,
            functionName: 'approve',
            args: [escrowAddress, amountWei],
            chain: targetChain,
          })
          pushStep('info', `approve tx enviada`, approveHash)
          const approveReceipt = await publicClient.waitForTransactionReceipt({
            hash: approveHash,
          })
          if (approveReceipt.status !== 'success') {
            throw new Error('La transacción de approve se revirtió on-chain')
          }
          pushStep('success', `approve confirmada en bloque ${approveReceipt.blockNumber}`)
        } else {
          pushStep('success', `Allowance suficiente (${formatAmount(allowance.toString())})`)
        }

        setStage('creating')
        pushStep(
          'progress',
          isTake ? 'Enviando takeOrder…' : 'Enviando createOrder…',
        )
        const hash = await walletClient.sendTransaction({
          to: calldata.to,
          data: calldata.data,
          value: 0n,
          chain: targetChain,
        })
        setTxHash(hash)
        pushStep('info', 'Tx enviada — esperando confirmación on-chain', hash)

        const receipt = await publicClient.waitForTransactionReceipt({ hash })
        if (receipt.status !== 'success') {
          throw new Error(
            'La transacción se revirtió on-chain (saldo insuficiente, allowance insuficiente u otra condición del contrato)',
          )
        }
        pushStep(
          'success',
          `Tx confirmada en bloque ${receipt.blockNumber} (${receipt.gasUsed.toString()} gas)`,
        )

        // Parse orderId from the OrderCreated log so we can poll backend.
        const onChainOrderId = extractOrderIdFromReceipt(receipt, calldata.to)
        if (onChainOrderId) {
          pushStep('info', `OrderID on-chain: ${shortHash(onChainOrderId)}`, onChainOrderId)

          setStage('matching')

          // Best-effort: report the confirmed order so the backend ingests it
          // immediately even if the chain-listener is behind. Non-blocking —
          // failure just falls back to the listener / reconcile cron.
          try {
            await apiClient.reportP2POrder(calldata.chain, onChainOrderId, hash)
            pushStep('info', 'Orden reportada al backend')
          } catch {
            pushStep('warn', 'No se pudo reportar (se ingerirá por el listener)')
          }

          await pollOrderStatus(onChainOrderId, isTake, pushStep)
        } else {
          pushStep('warn', 'No pude extraer el OrderID del receipt — saltando seguimiento backend')
        }

        setStage('success')
        toast.success(isTake ? 'Take enviada' : 'Orden creada')
        queryClient.invalidateQueries({ queryKey: ['p2p', 'orders', 'me'] })
        queryClient.invalidateQueries({ queryKey: ['p2p', 'orderbook'] })
        return { ok: true as const, hash, orderId: onChainOrderId }
      } catch (err) {
        const msg = humanizeP2PError(err)
        const detail = serializeError(err)
        setStage('error')
        setError(msg)
        pushStep('error', msg, detail)
        toast.error(msg)
        return { ok: false as const, error: msg }
      }
    },
    [address, walletClient, config, switchChainAsync, queryClient, pushStep],
  )

  const reset = useCallback(() => {
    setStage('idle')
    setError(null)
    setTxHash(null)
    setSteps([])
  }, [])

  return { createOrder, stage, error, txHash, steps, reset }
}

// ============================================================================
//  Helpers
// ============================================================================

async function pollOrderStatus(
  orderId: string,
  isTake: boolean,
  pushStep: (level: FlowStepLevel, message: string, detail?: string) => void,
) {
  pushStep('progress', 'Esperando que el matcher backend procese el evento…')
  let lastStatus: P2POrder['status'] | null = null
  let lastFilled = '0'
  for (let i = 0; i < MATCH_POLL_MAX; i++) {
    await sleep(MATCH_POLL_INTERVAL_MS)
    let order: P2POrder | null = null
    try {
      order = await apiClient.getP2POrder(orderId)
    } catch {
      // 404 from backend while the listener catches up — expected, keep polling.
      continue
    }
    if (!order) continue

    if (order.status !== lastStatus) {
      const label: P2POrder['status'] = order.status
      if (label === 'ACTIVE') {
        pushStep('info', isTake
          ? 'Take ACTIVE on-chain — matcher buscando contraparte…'
          : 'Orden ACTIVE en el orderbook — esperando que alguien la tome')
      } else if (label === 'PARTIAL') {
        pushStep('success', `Match parcial: ${formatAmount(order.filled)} / ${formatAmount(order.amount)}`)
      } else if (label === 'FILLED') {
        pushStep('success', `Orden completamente llenada (FILLED)`)
        return
      } else if (label === 'CANCELLED') {
        pushStep('warn', 'Orden CANCELLED')
        return
      } else if (label === 'EXPIRED') {
        pushStep('warn', 'Orden EXPIRED — no se encontró match a tiempo')
        return
      }
      lastStatus = label
    } else if (order.status === 'PARTIAL' && order.filled !== lastFilled) {
      pushStep('info', `Llenado parcial: ${formatAmount(order.filled)} / ${formatAmount(order.amount)}`)
      lastFilled = order.filled
    }

    // Para órdenes MAKER normales, ACTIVE es el estado de reposo (esperando
    // a un taker). No tiene sentido seguir polleando indefinidamente — corto
    // tras unos segundos y dejo el seguimiento al usuario.
    if (!isTake && order.status === 'ACTIVE' && i >= 4) {
      pushStep('info', 'La orden queda publicada — el matching ocurrirá cuando alguien la tome')
      return
    }
  }
  pushStep(
    'info',
    `Sigue ACTIVE tras ${Math.round((MATCH_POLL_MAX * MATCH_POLL_INTERVAL_MS) / 1000)}s — el match puede tardar más; podés cerrar este panel y monitorear en "Mis órdenes"`,
  )
}

function extractOrderIdFromReceipt(
  receipt: { logs: readonly { address: string; topics: readonly `0x${string}`[]; data: `0x${string}` }[] },
  escrowAddress: string,
): string | null {
  const escrowLower = escrowAddress.toLowerCase()
  for (const log of receipt.logs) {
    if (log.address.toLowerCase() !== escrowLower) continue
    try {
      const decoded = decodeEventLog({
        abi: P2P_ESCROW_ABI,
        data: log.data,
        topics: log.topics as [signature: `0x${string}`, ...args: `0x${string}`[]],
      })
      if (decoded.eventName === 'OrderCreated') {
        const args = decoded.args as unknown as { orderId: string }
        return args.orderId
      }
    } catch {
      // Not an OrderCreated log; keep scanning.
    }
  }
  return null
}

function shortAddr(addr: string): string {
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`
}

function shortHash(h: string): string {
  return `${h.slice(0, 10)}…${h.slice(-6)}`
}

function formatAmount(wei: string): string {
  try {
    const n = BigInt(wei)
    const whole = n / 10n ** 18n
    const frac = n % 10n ** 18n
    if (frac === 0n) return whole.toString()
    // 4 decimales de precisión es suficiente para el log
    const fracStr = (frac / 10n ** 14n).toString().padStart(4, '0').replace(/0+$/, '')
    return fracStr ? `${whole}.${fracStr}` : whole.toString()
  } catch {
    return wei
  }
}

function serializeError(err: unknown): string {
  if (!err) return ''
  if (err instanceof Error) {
    const parts: string[] = [err.message]
    const cause = (err as { cause?: unknown }).cause
    if (cause instanceof Error) parts.push(`cause: ${cause.message}`)
    const data = (err as { data?: unknown }).data
    if (data) parts.push(`data: ${JSON.stringify(data)}`)
    return parts.join('\n')
  }
  try {
    return JSON.stringify(err, null, 2)
  } catch {
    return String(err)
  }
}

function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms))
}
